import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import styled from 'styled-components';
import { Flex } from '../../box';
import { PopoverContentContainer } from '../../popover';
import Image from '../../image';
import { colors } from '../../../style';
import Text from '../../text';
import AppSwitcherNavButton from './app-switcher-nav-button';
import AppSwitcherButton from './app-switcher-button';

// assets
import poweredByGloat from './assets/powered-by-gloat.svg';

const APP_SWITCHER_NAV_ITEM_SIZE = 128;
const CONTAINER_OFF_SET = 20;

type AppSwitcherChildren =
  | React.ReactNode
  | ((props: { isOpen: boolean; onOpenChange: (open: boolean) => void }) => React.ReactNode);

const AppSwitcher = ({ children }: { children?: AppSwitcherChildren }): React.JSX.Element => {
  const [isOpen, setIsOpen] = React.useState(false);

  const onOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const renderedChildren = typeof children === 'function' ? children({ isOpen, onOpenChange }) : children;

  return (
    <DropdownMenu.Root modal={false} open={isOpen} onOpenChange={onOpenChange}>
      {renderedChildren}
    </DropdownMenu.Root>
  );
};

AppSwitcher.Trigger = ({ disabled }: { disabled?: boolean }) => (
  <DropdownMenu.Trigger asChild>
    <AppSwitcherButton disabled={disabled} />
  </DropdownMenu.Trigger>
);

const StyledDropdownMenuContent = styled(DropdownMenu.Content)`
  z-index: 9999;
  opacity: 1;
  animation: fadeInFromNone 0.15s ease;
  @keyframes fadeInFromNone {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }
`;

AppSwitcher.Container = ({ children }: { children: React.ReactNode }) => (
  <DropdownMenu.Portal>
    <StyledDropdownMenuContent sideOffset={CONTAINER_OFF_SET} collisionPadding={{ left: CONTAINER_OFF_SET }}>
      <PopoverContentContainer padding={'none'} width={432}>
        {children}
      </PopoverContentContainer>
    </StyledDropdownMenuContent>
  </DropdownMenu.Portal>
);

AppSwitcher.Section = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      <Flex padding={'xs'} gap={'xs'} flexWrap={'wrap'}>
        {children}
      </Flex>
      <DropdownMenu.Separator
        style={{
          border: '0',
          borderTop: `1px solid ${colors.gray100}`,
          borderColor: `${colors.gray300}`,
        }}
      />
    </>
  );
};

const StyledDropdownMenuItem = styled(DropdownMenu.Item)`
  outline: none;
  width: ${APP_SWITCHER_NAV_ITEM_SIZE}px;
  height: ${APP_SWITCHER_NAV_ITEM_SIZE}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

AppSwitcher.NavItem = ({
  icon,
  label,
  to,
  href,
  onClick,
}: {
  label: React.ReactNode;
  to?: string;
  href?: string;
  onClick?: () => void;
  icon: React.JSX.Element;
}) => {
  {
    /* items are initially rendered with focus state by design: https://github.com/radix-ui/primitives/issues/1164 */
  }
  return (
    <StyledDropdownMenuItem asChild>
      <AppSwitcherNavButton to={to} href={href} onClick={onClick}>
        {icon}
        <DropdownMenu.Label asChild>
          <Text align={'center'} weight={'inherit'} style={{ width: '100%', overflowWrap: 'break-word', height: 40 }}>
            {label}
          </Text>
        </DropdownMenu.Label>
      </AppSwitcherNavButton>
    </StyledDropdownMenuItem>
  );
};

AppSwitcher.Footer = () => (
  <Flex justifyContent={'center'} alignItems={'center'} padding={'md'}>
    <Text size={'md'} marginBottom={'xxs'} marginRight={'xs'} variant={'gray600'}>
      Powered by
    </Text>
        <DropdownMenu.Item asChild>
      <a href={'https://gloat.com'} target={'_blank'} rel={'noreferrer'} aria-label={'go to gloat website'}>
        <Image src={poweredByGloat} alt={''} />
      </a>
    </DropdownMenu.Item>
  </Flex>
);

export default AppSwitcher;
